import { FieldValues } from 'react-hook-form';

type ZohoField = {
  label: string;
  value: string;
};

type StudentApplicationPages = 'overview' | 'family' | 'biography' | 'housing' | 'dearFamily' | 'photoAlbum' | 'documents' | 'schoolAndLanguages';
type HostFamilyApplicationPages = 'familyOverview' | 'familyDetails' | 'houseHome' | 'community' | 'photos' | 'personalReferences' | 'agreement';

type FieldMapperType = {
  student: Record<StudentApplicationPages, Record<string, string>>;
  hostFamily: Record<HostFamilyApplicationPages, Record<string, string>>;
};

const fieldsMap: FieldMapperType = {
  student: {
    overview: {
      program: '',
      programGuaranteed: 'Guaranteed',
      lastName: 'Family_Last_Name',
      givenName: 'Given_First_Name_and_Middle_Name',
      nickName: 'Nickname',
      dateOfBirth: 'Birth_date',
      gender: 'Gender',
      cityOfBirth: 'City_of_Birth',
      countryOfBirth: 'Country_of_Birth',
      addressLine1: 'Street_Address',
      addressLine2: 'Address_Line_2',
      suburb: 'Suburb',
      city: 'City',
      country: 'Country',
      postalCode: 'Postal_Code',
      contactPhoneNumber: 'Telephone',
      contactEmailAddress: 'Email',
      emergencyContactPerson: 'Emergency_Contact',
      emergencyPhoneNumber: 'Emergency_Telephone_No',
      countryOfLegalResidence: 'Country_of_Legal_Permanent_Residence',
      countryOfCitizenship: 'Country_of_Citizenship',
    },
    family: {
      fatherStatus: '',
      fatherGuaranteed: '',
      fatherFirstName: '',
      fatherLastName: '',
      fatherOccupation: '',
      fatherCellPhone: '',
      fatherEmail: '',
      liveWithFather: '',
      motherStatus: '',
      motherSameAddress: '',
      motherFirstName: '',
      motherLastName: '',
      motherOccupation: '',
      motherCellPhone: '',
      motherEmail: '',
      liveWithMother: '',
      contactOption: '',
      parentsStatus: '',
      parentsStatusOther: '',
      homeType: '',
      homeLocation: '',
      relativeFirstName: '',
      relativeLastName: '',
      relativeRelationship: '',
      relativeEmail: '',
      relativePhone: '',
    },
    biography: {
      participatePreviousExchange: 'Participate_Previous_Exchange',
      willingToPayFees: 'Willing_To_Pay_School_Fees',
      feesRange: 'Fee_Amount',
      activity1: 'Activity_1',
      activity2: 'Activity_2',
      activity3: 'Activity_3',
      studentBio: 'Student_s_Bio',
      videoIntroductionLink: 'Student_s_Video_Introduction_Link',
      awardName: 'Award_Achievement_Name',
      awardInstitutionName: 'Institution_Name',
      awardReason: 'Reason_For_Receiving',
      awardDate: 'Month_Received',
    },
    housing: {
      kidsAttitude: 'Young_Children_Feelings',
      religionAttitude: 'Attend_Religious_Services',
      participateReligiousServices: '',
      religiousPreference: 'Religious_Preference',
      otherReligiousPreference: 'If_other_Religious_Preference_please_explain',
      smokingUnderstand: 'Understand_No_Smoking',
      smokingPolicy: '',
      myPets: 'What_kind_of_pets_do_you_have',
      otherPets: '',
      petsIndoor: 'Adjust_To_Indoor_Pet',
      otherPetsIndoor: 'Adjust_To_Indoor_Pet_Details',
      petFear: 'Afraid_Of_Pets',
      otherPetFear: '',
      doublePlacement: 'Accept_Double_Placement',
      singleParentPlacement: 'Accept_Single_Parent',
      sameSexCouplePlacement: 'Accept_Same_Sex_Couple',
      smallSchoolPlacement: 'Accept_Small_School',
      dietaryRestrictions: 'Diet_Restricted',
      dietaryRestrictionsExplanation: 'Please_list_any_foods_to_which_you_are_allergic',
      ownMealPreparation: 'Prepare_Own_Meals',
      vegetarianFamilyAdjustment: 'Adjust_To_Vegetarian',
      animalsWithFur: 'Are_you_allergic_to_animals_with_fur',
      dust: 'Are_you_allergic_to_dust',
      foodProducts: '',
      pollen: 'Are_you_allergic_to_pollen',
      otherMedicalConcerns: 'Other_Medical_Concerns',
    },
    dearFamily: {
      letter: '',
    },
    photoAlbum: {},
    documents: {},
    schoolAndLanguages: {},
  },

  hostFamily: {
    familyOverview: {},
    familyDetails: {
      familyDescription: 'Family_Description',
      familySpecialCircumstances: 'Special_Circumstances',
      typicalWeekday: 'Typical_Weekday',
      typicalWeekend: 'Typical_Weekend',
      specialWeekend: 'Special_Weekend',
      religiousAffiliation: 'Religion',
      religiousAffiliationOther: 'Other_Religion',
      religiousServiceFrequency: 'Religious_Attendence',
      studentAttendPreference: 'Prefer_Student_Attends',
      difficultIfNotSameReligion: 'Difficult_If_Not_Same_Religion',
      provideStudentMealsAgree: 'Agree_to_Provide_3_Meals_Per_Day',
      dietaryRestrictions: 'Dietary_Restrictions',
      dietaryRestrictionsExplain: 'Explanation_of_Dietary_Restrictions',
      studentFollowRestrictions: 'Expects_Student_to_Follow_Restrictions',
      comfortableStudentRestrictions: 'Comfortable_If_Student_Has_Restrictions',
      carInsurance: 'Auto_Insurance',
      firearms: 'Firearms',
      familySmoke: 'Smoking_in_Home',
      familyDrink: 'Drink_Alcohol',
      chronicIllness: 'Serious_Illness',
      chronicIllnessExplain: 'Serious_Illness_Explanation',
      mentalDisorder: 'Disability',
      mentalDisorderExplain: 'Disability_Explanation',
      convicted: 'Convicted_of_Crime',
      convictedExplain: 'Conviction_Explanation',
      contactWithChildServices: 'CPS_Contact',
      contactWithChildServicesExplain: 'CPS_Contact_Explanation',
      incomeRange: 'Income',
      financialStatus: 'Income_Explanation',
      publicAssistance: 'Public_Assistance',
      publicAssistanceExplain: 'Public_Assistance_Explanation',
    },
    houseHome: {},
    community: {
      cityPopulation: 'Population_of_Your_City_Town',
      cityWebsite: 'City_or_Town_Website',
      region: 'Region_of_U_S',
      nearestCity: 'Nearest_Major_City',
      nearestCityPopulation: 'Population_of_the_Nearest_Major_City',
      distanceFromCity: 'Distance_From_Nearest_Major_CityDistance_From_Near',
      communityDescription: 'Community_Description',
      placesAndEvents: 'Places_and_Events_of_Interest',
      areasToAvoid: 'Areas_to_Avoid',
      schoolTransport: 'Transportation_to_School',
      distanceToSchool: 'Distance_to_School',
      specialTransportation: 'Will_Transport_For_Activities',
      specialTransportationFrequency: 'How_Often_Will_Transport',
      contactWithCoach: 'Host_Coach_Contact',
      hostWorksAtSchool: 'Host_is_a_Coach',
      childrenSameSchool: 'Host_Siblings_at_Same_School',
      childrenSchoolActivities: 'Sibling_School_Activities',
    },
    photos: {},
    personalReferences: {},
    agreement: {},
  },
};

export const formatYesNoOptions = (value: boolean) => {
  return value ? 'yes' : 'no';
};

export const mapFieldsToZoho = <T extends keyof FieldMapperType>(fields: FieldValues, process: T, page: keyof FieldMapperType[T]) => {
  // Get list of map entries for the specific page
  const processFieldMap = fieldsMap[process][page] as Record<string, string>;

  // Assign the data based on the map
  const mappedData = Object.entries(fields).reduce((acc, [key, value]) => {
    const newKey = processFieldMap[key];
    if (newKey && value !== undefined) {
      acc.push({ label: newKey, value });
    }
    return acc;
  }, [] as ZohoField[]);
  return mappedData;
};

export const mapZohoFieldsToForm = (fields: Record<string, string>, process: keyof FieldMapperType) => {
  // Merge all nested objects into a single object for the process
  const processFieldMap = Object.values(fieldsMap[process]).reduce((acc, section) => {
    Object.assign(acc, section);
    return acc;
  }, {});

  // Reverse map
  const reversedMap = Object.fromEntries(Object.entries(processFieldMap).map(([key, value]) => [value, key]));
  const mappedData = Object.entries(fields).reduce((acc, [key, value]) => {
    const newKey = reversedMap[key];
    if (newKey) {
      acc[newKey] = value ?? '';
    }
    return acc;
  }, {} as FieldValues);

  return mappedData;
};
